/**
 * Customer API Module
 *
 * Handles customer-specific operations:
 * - Profile & usage
 * - Users
 * - Credentials
 * - ServiceNow instances
 * - Agent activities
 * - Notifications
 * - Upgrade requests
 */

import { http } from "./base";
import type {
  Customer,
  User,
  UsageStats,
  CustomerInstance,
  CredentialDisplay,
  CreateCredentialDto,
  Credential,
  UserCredentialDisplay,
  OrgCredentialDisplay,
  CreateUserCredentialDto,
  UpdateUserCredentialDto,
  CredentialAccessEntry,
  ServiceNowInstance,
  ServiceNowInstanceAccessEntry,
  AccessibleServiceNowInstance,
  CreateServiceNowInstanceDto,
  UpdateServiceNowInstanceDto,
  HealthStatus,
  SystemMetrics,
  DeletionFeedbackData,
  ServiceNowUpdateSet,
  ServiceNowUpdateXml,
} from "../types";

// ===== AGENT ACTIVITY TYPES =====

export interface AgentActivity {
  id: number;
  customerId: number;
  userId?: number;
  activityId: string;
  source: "jira" | "azure-devops" | "confluence" | "manual" | "request";
  storyId?: string;
  storyTitle: string;
  storyUrl?: string;
  storyType: "story" | "bug" | "task" | "feature" | "request" | "other";
  status:
    | "started"
    | "in_progress"
    | "review"
    | "completed"
    | "failed"
    | "cancelled";
  startedAt: number;
  completedAt?: number;
  reviewStartedAt?: number;
  reviewMetadata?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  summary?: string;
  errorMessage?: string;
  agentVersion?: string;
  updateSetName?: string;
  updateSetSysId?: string;
  updateSetUrl?: string;
  instanceUrl?: string;
  instanceName?: string;
  createdAt: number;
  updatedAt: number;
}

export interface AgentActivityArtifact {
  id: number;
  activityId: number;
  artifactType: string;
  artifactName: string;
  artifactSysId?: string;
  artifactUrl?: string;
  createdAt: number;
}

export interface AgentActivityWithArtifacts extends AgentActivity {
  artifacts: AgentActivityArtifact[];
}

export interface AgentActivityStats {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  inReview: number;
  bySource: Record<string, number>;
  byStoryType: Record<string, number>;
  recentCompletions: AgentActivity[];
}

export interface ActivityTrends {
  dailyActivities: Array<{
    date: string;
    total: number;
    completed: number;
    failed: number;
  }>;
  weeklyActivities: Array<{
    week: string;
    total: number;
    completed: number;
    failed: number;
  }>;
  sourceBreakdown: Array<{ source: string; count: number }>;
  completionRate: Array<{ date: string; rate: number }>;
}

// ===== NOTIFICATION TYPES =====

export interface NotificationSettings {
  emailEnabled: boolean;
  emailAddresses: string;
  emailOnComplete: boolean;
  emailOnFailed: boolean;
  digestMode: "instant" | "hourly" | "daily";
}

export interface InAppNotification {
  id: number;
  customerId: number;
  userId?: number;
  title: string;
  message: string;
  notificationType:
    | "activity_completed"
    | "activity_failed"
    | "system"
    | "info"
    | "warning";
  activityId?: number;
  artifactId?: number;
  actionUrl?: string;
  actionLabel?: string;
  isRead: boolean;
  readAt?: number;
  metadata?: Record<string, unknown>;
  createdAt: number;
  expiresAt?: number;
}

export interface NotificationHistoryItem {
  id: number;
  notificationType: "email";
  status: "sent" | "failed" | "queued";
  errorMessage?: string;
  sentAt?: number;
  createdAt: number;
  activity?: {
    activityId: string;
    storyTitle: string;
    source: string;
    status: string;
  };
}

export const customerApi = {
  // ===== PROFILE =====

  async getProfile(): Promise<{
    customer: Customer;
    usage: UsageStats;
    instances: CustomerInstance[];
  }> {
    const { data } = await http.get("/api/customer/profile");
    return data;
  },

  async getUsageStats(days: number = 30): Promise<UsageStats> {
    const { data } = await http.get<UsageStats>("/api/customer/usage", {
      params: { days },
    });
    return data;
  },

  async getUsageTimeline(
    days: number = 30,
    granularity: "hour" | "day" = "day",
  ): Promise<{
    success: boolean;
    timeline: Array<{ timestamp: number; date: string; calls: number }>;
  }> {
    const { data } = await http.get("/api/customer/usage/timeline", {
      params: { days, granularity },
    });
    return data;
  },

  async getToolUsage(days: number = 30): Promise<{
    success: boolean;
    tools: Array<{ toolName: string; count: number }>;
    categories: Record<string, number>;
    totalCalls: number;
    successRate: string;
  }> {
    const { data } = await http.get("/api/customer/usage/tools", {
      params: { days },
    });
    return data;
  },

  async getStats(): Promise<{
    totalUsers: number;
    usersByRole: { developer: number; stakeholder: number; admin: number };
    activeUsersByRole: {
      developer: number;
      stakeholder: number;
      admin: number;
    };
  }> {
    const { data } = await http.get<{
      success: boolean;
      stats: {
        totalUsers: number;
        usersByRole: { developer: number; stakeholder: number; admin: number };
        activeUsersByRole: {
          developer: number;
          stakeholder: number;
          admin: number;
        };
      };
    }>("/api/customer/stats");
    return data.stats;
  },

  // ===== USERS =====

  async getUsers(filters?: {
    status?: string;
    role?: string;
    limit?: number;
    offset?: number;
  }): Promise<User[]> {
    const { data } = await http.get<{
      users: User[];
      count: number;
      success: boolean;
    }>("/api/users", { params: filters });
    return data.users;
  },

  async getAllUsers(filters?: {
    status?: string;
    role?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ users: User[]; count: number }> {
    const { data } = await http.get<{
      users: User[];
      count: number;
      success: boolean;
    }>("/api/customer/users", { params: filters });
    return { users: data.users, count: data.count };
  },

  async createUser(userData: {
    username: string;
    email: string;
    password: string;
    role: "developer" | "stakeholder" | "admin";
    status?: "active" | "inactive" | "suspended";
  }): Promise<User> {
    const { data } = await http.post<{ user: User; success: boolean }>(
      "/api/customer/users",
      userData,
    );
    return data.user;
  },

  async updateUser(
    userId: string,
    updates: {
      username?: string;
      email?: string;
      password?: string;
      role?: "developer" | "stakeholder" | "admin";
      status?: "active" | "inactive" | "suspended";
      emailVerified?: boolean;
    },
  ): Promise<User> {
    const { data } = await http.patch<{ user: User; success: boolean }>(
      `/api/customer/users/${userId}`,
      updates,
    );
    return data.user;
  },

  async deleteUser(userId: string): Promise<void> {
    await http.delete(`/api/customer/users/${userId}`);
  },

  async updateSeats(
    developerSeats: number,
    stakeholderSeats: number,
  ): Promise<Customer> {
    const { data } = await http.patch("/api/customer/seats", {
      developerSeats,
      stakeholderSeats,
    });
    return data.customer;
  },

  // ===== CREDENTIALS (Organization) =====

  async getCredentials(): Promise<CredentialDisplay[]> {
    const { data } = await http.get<{ credentials: CredentialDisplay[] }>(
      "/api/credentials/list",
    );
    return data.credentials;
  },

  async addCredential(
    credentialData: CreateCredentialDto,
  ): Promise<Credential> {
    const { data } = await http.post<{ credential: Credential }>(
      "/api/credentials/store",
      credentialData,
    );
    return data.credential;
  },

  async updateCredential(
    id: number,
    updates: Partial<CreateCredentialDto>,
  ): Promise<Credential> {
    const { data } = await http.put<{ credential: Credential }>(
      `/api/credentials/${id}`,
      updates,
    );
    return data.credential;
  },

  async deleteCredential(service: string): Promise<void> {
    await http.delete(`/api/credentials/${service}`);
  },

  async testCredential(
    id: number,
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post<{ success: boolean; message: string }>(
      `/api/credentials/${id}/test`,
    );
    return data;
  },

  async updateCredentialScoping(
    service: string,
    scopeToUsers: boolean,
  ): Promise<{ success: boolean }> {
    const { data } = await http.put<{ success: boolean }>(
      `/api/credentials/${service}/scoping`,
      { scopeToUsers },
    );
    return data;
  },

  async getCredentialAccess(service: string): Promise<CredentialAccessEntry[]> {
    const { data } = await http.get<{ accessList: CredentialAccessEntry[] }>(
      `/api/credentials/${service}/access`,
    );
    return data.accessList;
  },

  async grantCredentialAccess(
    service: string,
    userId: number,
    accessType: "full" | "read_only" = "full",
  ): Promise<{ success: boolean }> {
    const { data } = await http.post<{ success: boolean }>(
      `/api/credentials/${service}/access`,
      { userId, accessType },
    );
    return data;
  },

  async revokeCredentialAccess(
    service: string,
    userId: number,
  ): Promise<{ success: boolean }> {
    const { data } = await http.delete<{ success: boolean }>(
      `/api/credentials/${service}/access/${userId}`,
    );
    return data;
  },

  // ===== USER CREDENTIALS (Personal) =====

  async getUserCredentials(): Promise<UserCredentialDisplay[]> {
    const { data } = await http.get<{ credentials: UserCredentialDisplay[] }>(
      "/api/user-credentials",
    );
    return data.credentials;
  },

  async getAccessibleOrgCredentials(): Promise<OrgCredentialDisplay[]> {
    const { data } = await http.get<{ credentials: OrgCredentialDisplay[] }>(
      "/api/user-credentials/org/accessible",
    );
    return data.credentials;
  },

  async testOrgCredential(
    service: string,
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post<{ success: boolean; message: string }>(
      `/api/user-credentials/org/${service}/test`,
    );
    return data;
  },

  async getUserCredential(service: string): Promise<UserCredentialDisplay> {
    const { data } = await http.get<{ credential: UserCredentialDisplay }>(
      `/api/user-credentials/${service}`,
    );
    return data.credential;
  },

  async addUserCredential(
    credentialData: CreateUserCredentialDto,
  ): Promise<UserCredentialDisplay> {
    const { data } = await http.post<{ credential: UserCredentialDisplay }>(
      "/api/user-credentials",
      credentialData,
    );
    return data.credential;
  },

  async updateUserCredential(
    service: string,
    updates: UpdateUserCredentialDto,
  ): Promise<UserCredentialDisplay> {
    const { data } = await http.put<{ credential: UserCredentialDisplay }>(
      `/api/user-credentials/${service}`,
      updates,
    );
    return data.credential;
  },

  async deleteUserCredential(service: string): Promise<void> {
    await http.delete(`/api/user-credentials/${service}`);
  },

  async testUserCredential(
    service: string,
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post<{ success: boolean; message: string }>(
      `/api/user-credentials/${service}/test`,
    );
    return data;
  },

  // ===== SERVICENOW INSTANCES =====

  async getInstances(): Promise<ServiceNowInstance[]> {
    const { data } = await http.get<{ instances: ServiceNowInstance[] }>(
      "/api/servicenow/instances",
    );
    return data.instances;
  },

  async getInstance(id: number): Promise<ServiceNowInstance> {
    const { data } = await http.get<{ instance: ServiceNowInstance }>(
      `/api/servicenow/instances/${id}`,
    );
    return data.instance;
  },

  async createInstance(
    instanceData: CreateServiceNowInstanceDto,
  ): Promise<ServiceNowInstance> {
    const { data } = await http.post<{ instance: ServiceNowInstance }>(
      "/api/servicenow/instances",
      instanceData,
    );
    return data.instance;
  },

  async updateInstance(
    id: number,
    updates: UpdateServiceNowInstanceDto,
  ): Promise<ServiceNowInstance> {
    const { data } = await http.put<{ instance: ServiceNowInstance }>(
      `/api/servicenow/instances/${id}`,
      updates,
    );
    return data.instance;
  },

  async deleteInstance(id: number): Promise<void> {
    await http.delete(`/api/servicenow/instances/${id}`);
  },

  async testInstance(
    id: number,
  ): Promise<{ success: boolean; message: string; details?: unknown }> {
    const { data } = await http.post<{
      success: boolean;
      message: string;
      details?: unknown;
    }>(`/api/servicenow/instances/${id}/test`);
    return data;
  },

  async setDefaultInstance(id: number): Promise<void> {
    await http.post(`/api/servicenow/instances/${id}/set-default`);
  },

  async updateInstanceScoping(
    id: number,
    scopeToUsers: boolean,
  ): Promise<{ success: boolean }> {
    const { data } = await http.put<{ success: boolean }>(
      `/api/servicenow/instances/${id}/scoping`,
      { scopeToUsers },
    );
    return data;
  },

  async getInstanceAccess(
    id: number,
  ): Promise<ServiceNowInstanceAccessEntry[]> {
    const { data } = await http.get<{
      access: ServiceNowInstanceAccessEntry[];
    }>(`/api/servicenow/instances/${id}/access`);
    return data.access;
  },

  async grantInstanceAccess(
    id: number,
    userId: number,
    accessType: "full" | "read_only" = "full",
  ): Promise<{ success: boolean }> {
    const { data } = await http.post<{ success: boolean }>(
      `/api/servicenow/instances/${id}/access`,
      { userId, accessType },
    );
    return data;
  },

  async revokeInstanceAccess(
    id: number,
    userId: number,
  ): Promise<{ success: boolean }> {
    const { data } = await http.delete<{ success: boolean }>(
      `/api/servicenow/instances/${id}/access/${userId}`,
    );
    return data;
  },

  // Enterprise User accessible instances
  async getAccessibleInstances(): Promise<AccessibleServiceNowInstance[]> {
    const { data } = await http.get<{
      instances: AccessibleServiceNowInstance[];
    }>("/api/user-credentials/servicenow/instances");
    return data.instances;
  },

  async getDefaultInstance(): Promise<AccessibleServiceNowInstance | null> {
    try {
      const { data } = await http.get<{
        instance: AccessibleServiceNowInstance;
      }>("/api/user-credentials/servicenow/default");
      return data.instance;
    } catch {
      return null;
    }
  },

  // ===== SERVICENOW UPDATE SETS =====

  async getUpdateSets(params?: {
    days?: number;
    state?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    updateSets: ServiceNowUpdateSet[];
    count: number;
    offset: number;
    limit: number;
    days: number;
    error?: string;
    message?: string;
  }> {
    const { data } = await http.get("/api/servicenow/update-sets", { params });
    return data;
  },

  async getUpdateSetUpdates(
    sysId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<{
    success: boolean;
    updates: ServiceNowUpdateXml[];
    count: number;
    error?: string;
  }> {
    const { data } = await http.get(
      `/api/servicenow/update-sets/${sysId}/updates`,
      { params },
    );
    return data;
  },

  // ===== ARTIFACT PREVIEW =====

  async searchPreviewWidgets(
    query: string,
    limit: number = 20,
  ): Promise<{ widgets: Record<string, unknown>[]; totalFound: number }> {
    const { data } = await http.get("/api/artifact-preview/widgets", {
      params: { query, limit },
    });
    return data.data;
  },

  async searchPreviewArtifacts(
    query: string,
    type: string = "all",
    limit: number = 20,
    instanceId?: number | null,
  ): Promise<{ artifacts: Record<string, unknown>[]; totalFound: number }> {
    const { data } = await http.get("/api/artifact-preview/artifacts", {
      params: { query, type, limit, ...(instanceId != null ? { instanceId } : {}) },
    });
    return data.data;
  },

  async getPreviewWidget(sysId: string): Promise<Record<string, unknown>> {
    const { data } = await http.get(`/api/artifact-preview/widgets/${sysId}`);
    return data.data;
  },

  async getPreviewArtifact(
    type: string,
    sysId: string,
    instanceId?: number | null,
  ): Promise<{ type: string; artifact: Record<string, unknown> }> {
    const { data } = await http.get(
      `/api/artifact-preview/artifact/${type}/${sysId}`,
      { params: instanceId != null ? { instanceId } : undefined },
    );
    return data.data;
  },

  async getFlowStructure(sysId: string, instanceId?: number | null): Promise<Record<string, unknown>> {
    const { data } = await http.get(
      `/api/artifact-preview/flow/${sysId}/structure`,
      { params: instanceId != null ? { instanceId } : undefined },
    );
    return data.data;
  },

  async getPageLayout(
    sysId: string,
    includeWidgets?: boolean,
    instanceId?: number | null,
  ): Promise<{
    page: Record<string, unknown>;
    instances: Record<string, unknown>[];
    containers: Record<string, unknown>[];
    widgets?: Record<string, { name: string; template: string; css: string; script: string; client_script: string }>;
    columns?: Record<string, { size: number; order: number }>;
    themeCss?: string;
  }> {
    const { data } = await http.get(
      `/api/artifact-preview/page/${sysId}/layout`,
      {
        params: {
          ...(includeWidgets ? { include_widgets: 'true' } : {}),
          ...(instanceId != null ? { instanceId } : {}),
        },
      },
    );
    return data.data;
  },

  async parseDataShape(
    serverScript: string,
  ): Promise<{ shape: Record<string, unknown> }> {
    const { data } = await http.post("/api/artifact-preview/parse-data-shape", {
      serverScript,
    });
    return data.data;
  },

  // The white-label theme methods (/api/themes/*) were removed: the backend
  // reads a theme directory that isn't in the Docker image, so the list was
  // permanently empty, and no caller survived the org-unification migration.

  // ===== AGENT ACTIVITIES =====

  async getActivities(options?: {
    status?: string;
    source?: string;
    days?: number;
    limit?: number;
    offset?: number;
    search?: string;
    sortBy?: string;
  }): Promise<{ activities: AgentActivity[]; total: number }> {
    const { data } = await http.get<{
      activities: AgentActivity[];
      total: number;
      count: number;
      success: boolean;
    }>("/api/agent/dashboard/activities", { params: options });
    return { activities: data.activities, total: data.total };
  },

  async getActivityStats(days: number = 30): Promise<AgentActivityStats> {
    const { data } = await http.get<{
      stats: AgentActivityStats;
      success: boolean;
    }>("/api/agent/dashboard/stats", { params: { days } });
    return data.stats;
  },

  async getActivity(activityId: string): Promise<AgentActivityWithArtifacts> {
    const { data } = await http.get<{
      activity: AgentActivityWithArtifacts;
      success: boolean;
    }>(`/api/agent/dashboard/activity/${activityId}`);
    return data.activity;
  },

  async deleteActivity(activityId: string): Promise<void> {
    await http.delete(`/api/agent/dashboard/activity/${activityId}`);
  },

  async exportActivities(options?: {
    days?: number;
    status?: string;
    source?: string;
    search?: string;
  }): Promise<{
    activities: AgentActivity[];
    total: number;
    truncated: boolean;
  }> {
    const { data } = await http.get<{
      activities: AgentActivity[];
      total: number;
      count: number;
      truncated: boolean;
      success: boolean;
    }>("/api/agent/dashboard/activities/export", { params: options });
    return {
      activities: data.activities,
      total: data.total,
      truncated: data.truncated,
    };
  },

  async getActivityTrends(days: number = 30): Promise<ActivityTrends> {
    const { data } = await http.get<{
      trends: ActivityTrends;
      success: boolean;
    }>("/api/agent/dashboard/trends", { params: { days } });
    return data.trends;
  },

  // ===== NOTIFICATIONS =====

  async getNotificationSettings(): Promise<NotificationSettings> {
    const { data } = await http.get<{
      settings: NotificationSettings;
      success: boolean;
    }>("/api/agent/notifications/settings");
    return data.settings;
  },

  async updateNotificationSettings(
    settings: Partial<NotificationSettings>,
  ): Promise<NotificationSettings> {
    const { data } = await http.put<{
      settings: NotificationSettings;
      success: boolean;
    }>("/api/agent/notifications/settings", settings);
    return data.settings;
  },

  async testNotification(
    type: "email",
  ): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post<{ success: boolean; message: string }>(
      "/api/agent/notifications/test",
      { type },
    );
    return data;
  },

  async getInAppNotifications(options?: {
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<InAppNotification[]> {
    const { data } = await http.get<{
      notifications: InAppNotification[];
      success: boolean;
    }>("/api/agent/notifications/in-app", {
      params: {
        unread_only: options?.unreadOnly ? "true" : "false",
        limit: options?.limit || 50,
        offset: options?.offset || 0,
      },
    });
    return data.notifications;
  },

  async getUnreadNotificationCount(): Promise<number> {
    const { data } = await http.get<{ count: number; success: boolean }>(
      "/api/agent/notifications/in-app/count",
    );
    return data.count;
  },

  async markNotificationAsRead(notificationId: number): Promise<void> {
    await http.post(`/api/agent/notifications/in-app/${notificationId}/read`);
  },

  async markAllNotificationsAsRead(): Promise<number> {
    const { data } = await http.post<{ count: number; success: boolean }>(
      "/api/agent/notifications/in-app/read-all",
    );
    return data.count;
  },

  async deleteNotification(notificationId: number): Promise<void> {
    await http.delete(`/api/agent/notifications/in-app/${notificationId}`);
  },

  async getNotificationHistory(options?: {
    days?: number;
    limit?: number;
    offset?: number;
  }): Promise<NotificationHistoryItem[]> {
    const { data } = await http.get<{
      history: NotificationHistoryItem[];
      success: boolean;
    }>("/api/agent/notifications/history", {
      params: {
        days: options?.days || 30,
        limit: options?.limit || 100,
        offset: options?.offset || 0,
      },
    });
    return data.history;
  },

  // ===== UPGRADE REQUESTS =====

  // The two /api/upgrade-requests/customer methods that stood here were
  // removed in wave 5 with the routes behind them. They filed
  // `customer_developer_seats` / `customer_stakeholder_seats` rows against the
  // `customers` table migration 114 dropped, so they could not have succeeded
  // for any input. The live enterprise inquiry runs through
  // `portalApi.requestEnterprisePlan` from pages/customer/Billing.tsx.

  // ===== MONITORING =====

  async getHealth(): Promise<HealthStatus> {
    const { data } = await http.get<HealthStatus>(
      "/api/monitoring/health/detailed",
    );
    return data;
  },

  async getMetrics(): Promise<SystemMetrics> {
    const { data } = await http.get<SystemMetrics>("/api/monitoring/metrics", {
      params: { format: "json" },
    });
    return data;
  },

  async getDeletionFeedback(): Promise<DeletionFeedbackData> {
    const { data } = await http.get<DeletionFeedbackData>(
      "/api/monitoring/deletion-feedback",
    );
    return data;
  },
};
